/* Non-dismissible modal that blocks a professional from using the app until
 * they upload a profile photo. Triggered by `user.needs_profile_photo === true`
 * which the backend returns for any artisan whose `profile_photo` field is
 * empty (legacy accounts that registered before the mandatory-photo check
 * was added).
 *
 * UX intent:
 *   - Cannot be dismissed (no X button, no overlay-click-to-close).
 *   - Single clear CTA: pick a photo.
 *   - Same client-side validation as registration (>=8KB, <=12MB, MIME +
 *     image probe) so we never ship a corrupt file to the backend.
 *   - Uploads via the existing `/api/artisan/upload-profile-photo` endpoint
 *     which runs the AI Ghana-Card face match and auto-approves on success.
 */
import { useRef, useState } from "react";
import axios from "axios";
import { Camera, Upload, AlertCircle, CheckCircle2 } from "lucide-react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import CameraSheet from "@/components/CameraSheet";
import { toast } from "sonner";

export default function ForceProfilePhotoUpload() {
  const { token, user, setUser } = useAuth();
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cam, setCam] = useState(false);

  /**
   * Mirror the same defensive validation used in RegisterPage so the artisan
   * can't accidentally upload an empty / corrupt photo and get stuck again.
   */
  const handleFile = (file) => {
    if (!file) return;
    if (file.size < 8 * 1024) {
      toast.error("Photo too small · retake a clear headshot");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Photo too large · max 12MB");
      return;
    }
    const mime = (file.type || "").toLowerCase();
    if (mime && !/^image\/(jpeg|jpg|png|webp|heic|heif)$/.test(mime)) {
      toast.error("Please upload a JPG, PNG, WebP or HEIC headshot.");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (!result || typeof result !== "string" || result.length < 8 * 1024) {
        toast.error("That photo couldn't be read. Please try a different image.");
        return;
      }
      // Decode probe — if <img> can't render it, we don't ship it.
      const probe = new Image();
      probe.onload = () => {
        setSelectedFile(file);
        setPreviewUrl(result);
      };
      probe.onerror = () => {
        toast.error("That image is corrupted · try another");
      };
      probe.src = result;
    };
    reader.onerror = () => toast.error("Couldn't read that file. Please try again.");
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      // The existing endpoint expects multipart/form-data with a `file` field
      // and reads the auth token from the query string (matches the rest of
      // the API surface).
      const form = new FormData();
      form.append("file", selectedFile);
      await axios.post(`${API}/artisan/upload-profile-photo?token=${token}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      // Refresh the auth state. The new /auth/me returns
      // needs_profile_photo=false now that the photo exists, which closes
      // this modal automatically.
      const me = await axios.get(`${API}/auth/me?token=${token}`);
      setUser(me.data);
      localStorage.setItem("yorm_user", JSON.stringify(me.data));
      toast.success("Photo uploaded. Thanks for completing your profile!");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed. Please try a different photo.";
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1250] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="force-profile-photo-overlay"
      // Block click-through dismissal — there is no path forward except upload.
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-[#4F988E]/10 mb-4">
          <Camera className="h-7 w-7 text-[#4F988E]" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-center text-[#222] mb-2">
          Add your profile photo
        </h2>
        <p className="text-sm text-gray-600 text-center mb-5 leading-relaxed">
          Hi {user?.full_name?.split(" ")[0] || "there"}, every Yorm professional now needs a clear headshot
          so customers can recognise you. Please take a moment to add yours — it only takes 10 seconds.
        </p>

        {/* Photo picker / preview */}
        {previewUrl ? (
          <div className="mb-4">
            <div className="relative w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-[#4F988E] shadow-md">
              <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
              <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1.5 border-2 border-white">
                <CheckCircle2 className="h-4 w-4 text-white" />
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setPreviewUrl(null);
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              className="block mx-auto mt-3 text-sm text-[#4F988E] underline"
              data-testid="force-photo-retake-btn"
            >
              Choose a different photo
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button
              type="button"
              onClick={() => setCam(true)}
              className="border-2 border-dashed border-[#4F988E] hover:bg-[#4F988E]/5 transition rounded-xl p-5 text-center"
              data-testid="force-photo-camera"
            >
              <Camera className="h-7 w-7 text-[#4F988E] mx-auto mb-2" />
              <p className="text-sm font-medium text-[#222]">Take a selfie</p>
            </button>
            <label
              htmlFor="force-profile-photo-input"
              className="block border-2 border-dashed border-gray-300 hover:border-[#4F988E] hover:bg-[#4F988E]/5 transition rounded-xl p-5 text-center cursor-pointer"
              data-testid="force-photo-picker"
            >
              <Upload className="h-7 w-7 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-[#222]">Upload a photo</p>
            </label>
            <p className="col-span-2 text-xs text-gray-500 text-center">JPG, PNG, or HEIC · at least 8KB · up to 12MB</p>
          </div>
        )}
        <CameraSheet open={cam} onOpenChange={setCam} onCapture={handleFile} facing="user" title="Profile photo" />
        <input
          ref={fileInputRef}
          id="force-profile-photo-input"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="force-photo-input"
        />

        {/* Helper text — why we ask */}
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 mb-5">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Accounts without a profile photo cannot accept bookings. Please use a recent clear headshot
            of yourself — not a logo, a friend, or stock art.
          </span>
        </div>

        <Button
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          className="w-full h-12 rounded-xl bg-[#4F988E] hover:bg-[#3d7a72] text-white font-semibold text-base"
          data-testid="force-photo-upload-btn"
        >
          {uploading ? "Uploading…" : "Submit Profile Photo"}
        </Button>
      </div>
    </div>
  );
}
